<template>
  <div class="flex items-center w-full p-0 select-box hover_border-gray-300">
    <div class="w-full options-container">
      <div class="search-box">
        <VInput
          v-model="searchTerm"
          theme="gray"
          class="h-10"
          :class="{ 'mb-4': hasGap }"
          :placeholder="placeholder"
          :disabled="disabled"
          @update:model-value="$emit('update:search', searchTerm)"
        >
          <template #icon="{ error }">
            <MagnifyingGlassIcon class="w-5 h-5 ml-4 text-gray-700" :class="{ 'text-red-800': error }" />
          </template>
          <template #button>
            <XMarkIcon
              v-if="search"
              class="w-4 h-4 mr-4 text-gray-800 cursor-pointer hover_text-black"
              @click="$emit('update:search', '')"
            />
          </template>
        </VInput>
      </div>
      <div class="overflow-y-auto options-inner-container h-80">
        <div v-if="!loading && pageCount === 0" class="pt-2">
          <slot name="no-results">
            <div class="mx-auto mt-12 font-light text-center text-gray-600 mb-14">
              <XCircleIcon class="inline-block mb-4 w-14 h-14" />
              <p class="mb-0 text-xl">Sorry!</p>
              <p class="mb-0">No results were found.<br />Please try some other keywords.</p>
            </div>
          </slot>
        </div>
        <div v-else class="relative">
          <div v-if="loading && (!autoLoad || pageCount === 0)" class="flex items-center justify-center w-full h-full">
            <FontAwesomeIcon class="text-gray-300" icon="circle-notch" spin="spin" size="3x" />
          </div>
          <slot v-else />
          <div ref="LoadMoreRef" class="w-full h-1">&nbsp;</div>
        </div>
      </div>
      <div v-if="!loading && !autoLoad && count > pageCount" class="pt-2 text-sm text-gray-400">
        Showing only first {{ pageCount }} results
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { XCircleIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/solid'

export interface Option {
  id: string | number
  name: string
}

const props = defineProps({
  pageCount: {
    type: Number,
    required: true,
  },
  count: {
    type: Number,
    default: null,
  },
  placeholder: {
    type: String,
    default: 'Search\u2026',
  },
  search: {
    type: String as PropType<string | null>,
    default: '',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  autoLoad: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  hasGap: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:search', 'load-more'])

const { LoadMoreRef } = useDetectViewportVisibility((entries: IntersectionObserverEntry[]) => {
  if (entries[0].intersectionRatio == 1) {
    emit('load-more', entries)
  }
})

const searchTerm = ref<string>(props.search || '')

watch(
  () => props.search,
  value => {
    searchTerm.value = value || ''
  }
)
</script>

<style lang="stylus" scoped>
.select-box
  .options-inner-container
    &::-webkit-scrollbar
      width 8px
      background rgba(0,0,0,0.12)
      border-radius 4px

    &::-webkit-scrollbar-thumb
      background rgba(0,0,0,0.12)
      border-radius 4px
</style>
